import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

export type InvestPageNavMeta = {
  subTitle: string;
  onBackButtonClick?: () => void;
  navRightContent?: ReactNode;
};

/**
 * Deal counts shown on the Invest tabs, keyed by DealTypeEnumType value. Kept out of
 * navMeta because setNavMeta replaces the whole object, and pages that do not know
 * about counts (Deal Invites) would otherwise wipe them on mount.
 */
export type InvestDealTypeCounts = Record<string, number>;

const defaultMeta: InvestPageNavMeta = {
  subTitle: '',
  onBackButtonClick: undefined,
};

const InvestPageNavContext = createContext<{
  navMeta: InvestPageNavMeta;
  setNavMeta: (meta: InvestPageNavMeta) => void;
  dealTypeCounts: InvestDealTypeCounts;
  setDealTypeCounts: (counts: InvestDealTypeCounts) => void;
  hideTabHighlight: boolean;
  setHideTabHighlight: (hide: boolean) => void;
} | null>(null);

export function InvestPageNavProvider({ children }: { children: ReactNode }) {
  const [navMeta, setNavMetaState] = useState<InvestPageNavMeta>(defaultMeta);
  const [dealTypeCounts, setDealTypeCountsState] = useState<InvestDealTypeCounts>({});
  const [hideTabHighlight, setHideTabHighlight] = useState(false);

  const setNavMeta = useCallback((meta: InvestPageNavMeta) => {
    setNavMetaState(meta);
  }, []);

  const setDealTypeCounts = useCallback((counts: InvestDealTypeCounts) => {
    setDealTypeCountsState(counts);
  }, []);

  const value = useMemo(
    () => ({
      navMeta,
      setNavMeta,
      dealTypeCounts,
      setDealTypeCounts,
      hideTabHighlight,
      setHideTabHighlight,
    }),
    [navMeta, setNavMeta, dealTypeCounts, setDealTypeCounts, hideTabHighlight]
  );

  return <InvestPageNavContext.Provider value={value}>{children}</InvestPageNavContext.Provider>;
}

export function useInvestPageNav() {
  const ctx = useContext(InvestPageNavContext);
  if (!ctx) {
    throw new Error('useInvestPageNav must be used within InvestPageNavProvider');
  }
  return ctx;
}
